import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Dimensions,
  TextInput,
  Platform,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { WebView } from 'react-native-webview';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import * as Location from 'expo-location';
import { apiService, Place } from '../services/api';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { looksLikeMapsLink, parseCoordinateText, parseMapsLinkCoordinates } from '../utils/maps-link.util';

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

interface PickedLocation {
  latitude: number;
  longitude: number;
  address: string;
  /** Reverse-geocoded country/city, when the underlying provider returns structured address
   * parts (both Nominatim and expo-location do) — lets callers auto-select a country picklist
   * instead of asking the user to re-type it. Absent (not empty string) when unavailable. */
  country?: string;
  city?: string;
}

interface MapLocationPickerProps {
  visible: boolean;
  onClose: () => void;
  onLocationSelect: (location: PickedLocation) => void;
  /** When provided (e.g. edit mode with saved location), map centers here and uses it as selected */
  initialLocation?: PickedLocation | null;
}

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  visible,
  onClose,
  onLocationSelect,
  initialLocation,
}) => {
  const styles = useThemedStyles(getStyles);
  const [currentLocation, setCurrentLocation] = useState<PickedLocation | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<PickedLocation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<Place[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const webViewRef = useRef<WebView>(null);
  const { t } = useLocale();
  // Google / Apple Maps links (and plain "lat, lng") pasted into the search
  // box resolve straight to that spot — available everywhere this picker is.
  const [resolvingLink, setResolvingLink] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const linkSeqRef = useRef(0);
  // Guards against a slow, superseded nearby-pins request overwriting a
  // newer one's results — panning fires a fresh requestPlaces roughly every
  // second, and Overpass (a shared public instance) can occasionally still
  // take several seconds per call despite the backend's own caching/query
  // tightening; without this, whichever request happens to resolve LAST
  // wins, which isn't necessarily the most recent one asked.
  const nearbyPinsRequestSeqRef = useRef(0);

  // Set up message listener for web iframe
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      // @ts-ignore - Attach handler to window for iframe messages
      window.handleMapMessage = handleMapMessage;
      
      const messageHandler = (event: MessageEvent) => {
        const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
        if (iframe && event.source === iframe.contentWindow) {
          handleMapMessage(event);
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      return () => {
        window.removeEventListener('message', messageHandler);
        // @ts-ignore
        delete window.handleMapMessage;
      };
    }
  }, [visible]);

  // When modal opens: use initialLocation (edit mode with saved lat/lon) or fetch current location
  useEffect(() => {
    if (!visible) return;
    setSearchQuery('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    const hasInitial =
      initialLocation &&
      typeof initialLocation.latitude === 'number' &&
      typeof initialLocation.longitude === 'number';
    if (hasInitial) {
      const loc = {
        latitude: initialLocation!.latitude,
        longitude: initialLocation!.longitude,
        address: initialLocation!.address || 'Saved location',
      };
      setCurrentLocation(loc);
      setSelectedLocation(loc);
      setIsLoading(false);
    } else {
      setSelectedLocation(null);
      getCurrentLocation();
    }
  }, [visible, initialLocation]);

  // Center map when currentLocation changes (after map is loaded)
  useEffect(() => {
    if (currentLocation && visible) {
      const centerMap = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(JSON.stringify({
              type: 'centerMap',
              latitude: currentLocation.latitude,
              longitude: currentLocation.longitude,
            }), '*');
          }
        } else if (webViewRef.current) {
          webViewRef.current.postMessage(JSON.stringify({
            type: 'centerMap',
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
          }));
        }
      };

      // Wait a bit to ensure map is loaded
      const timeout = setTimeout(centerMap, 500);
      return () => clearTimeout(timeout);
    }
  }, [currentLocation, visible]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const getCurrentLocation = async () => {
    try {
      setIsLoading(true);
      
      if (Platform.OS === 'web') {
        // Use browser geolocation API for web
        if (typeof navigator !== 'undefined' && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const latitude = position.coords.latitude;
              const longitude = position.coords.longitude;

              // Get address for current location
              const geocoded = await getAddressFromCoordinates(latitude, longitude);

              const locationData = {
                latitude,
                longitude,
                address: geocoded.formatted,
                country: geocoded.country,
                city: geocoded.city,
              };

              setCurrentLocation(locationData);
              setSelectedLocation(locationData);
              setIsLoading(false);
            },
            (error) => {
              console.error('Error getting current location:', error);
              Alert.alert(
                'Location Error',
                'Failed to get your current location. Please try again.',
                [{ text: 'OK' }]
              );
              setIsLoading(false);
            },
            {
              enableHighAccuracy: false,
              timeout: 10000,
              maximumAge: 300000, // 5 minutes
            }
          );
        } else {
          Alert.alert(
            'Location Unavailable',
            'Geolocation is not supported in your browser.',
            [{ text: 'OK' }]
          );
          setIsLoading(false);
        }
      } else {
        // Use expo-location for mobile
        const { status } = await Location.requestForegroundPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            'Permission Required',
            'Please grant access to your location to use the location picker.',
            [{ text: 'OK' }]
          );
          setIsLoading(false);
          return;
        }

        const location = await Location.getCurrentPositionAsync({
          accuracy: Location.Accuracy.Balanced,
        });

        // Get address for current location
        const geocoded = await getAddressFromCoordinates(
          location.coords.latitude,
          location.coords.longitude
        );

        const locationData = {
          latitude: location.coords.latitude,
          longitude: location.coords.longitude,
          address: geocoded.formatted,
          country: geocoded.country,
          city: geocoded.city,
        };

        setCurrentLocation(locationData);
        setSelectedLocation(locationData);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error getting current location:', error);
      Alert.alert(
        'Location Error',
        'Failed to get your current location. Please try again.',
        [{ text: 'OK' }]
      );
      setIsLoading(false);
    }
  };

  const getAddressFromCoordinates = async (latitude: number, longitude: number): Promise<{ formatted: string; country?: string; city?: string }> => {
    try {
      if (Platform.OS === 'web') {
        // Use Nominatim (OpenStreetMap) for reverse geocoding on web
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
          {
            headers: {
              'User-Agent': 'SendifierApp/1.0',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Reverse geocoding failed');
        }

        const data = await response.json();
        if (data && data.address) {
          const addr = data.address;
          const parts = [
            addr.road,
            addr.house_number,
            addr.neighbourhood || addr.suburb,
            addr.city || addr.town || addr.village,
            addr.state || addr.region,
            addr.country,
          ].filter(Boolean);
          return {
            formatted: parts.join(', ') || data.display_name || 'Unknown Location',
            country: addr.country,
            city: addr.city || addr.town || addr.village,
          };
        }
        return { formatted: data.display_name || 'Unknown Location' };
      } else {
        // Use expo-location for mobile
        const reverseGeocode = await Location.reverseGeocodeAsync({
          latitude,
          longitude,
        });

        if (reverseGeocode.length > 0) {
          const addr = reverseGeocode[0];
          return {
            formatted: [
              addr.street,
              addr.city,
              addr.region,
              addr.country,
            ].filter(Boolean).join(', ') || 'Unknown Location',
            country: addr.country || undefined,
            city: addr.city || undefined,
          };
        }
        return { formatted: 'Unknown Location' };
      }
    } catch (error) {
      console.warn('Reverse geocoding failed:', error);
      return { formatted: 'Unknown Location' };
    }
  };

  // Fetch search suggestions as user types — backed by the self-hosted
  // Nominatim instance, not the Overture Places dataset (GET
  // /api/places/search, this used to call): that route is disabled in
  // production, since the `duckdb` native addon it depends on fails to
  // compile against prod's GCC 13 (see sendifier_api's src/routes/index.ts).
  // Nominatim's response is adapted into the same Place shape the dropdown
  // below already renders, so nothing past this function needs to change.
  const fetchSearchSuggestions = async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await apiService.searchAddressSuggestions(query, 8);
      if (response.data) {
        const asPlaces: Place[] = response.data.map((item, index) => {
          const firstSegment = item.address?.split(',')[0]?.trim();
          return {
            overture_id: `nominatim-${index}-${item.latitude}-${item.longitude}`,
            latitude: item.latitude,
            longitude: item.longitude,
            name_primary: firstSegment || item.address || '',
            name_ar: null,
            name_en: null,
            category: null,
            address_freeform: item.address || null,
            locality: item.city || null,
          };
        });
        setSearchSuggestions(asPlaces);
        setShowSuggestions(true);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSearchSuggestions([]);
    }
  };

  const centerMapOn = (latitude: number, longitude: number) => {
    setTimeout(() => {
      const message = JSON.stringify({ type: 'centerMap', latitude, longitude });
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
        iframe?.contentWindow?.postMessage(message, '*');
      } else {
        webViewRef.current?.postMessage(message);
      }
    }, 300);
  };

  /** Search-box text that is a maps link or coordinates → select + center.
   * Returns false when the text is neither (normal place search applies). */
  const resolveLinkOrCoordinates = async (text: string): Promise<boolean> => {
    const coords = parseCoordinateText(text);
    const isLink = looksLikeMapsLink(text);
    if (!coords && !isLink) return false;

    const seq = ++linkSeqRef.current;
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setLinkError(null);
    setResolvingLink(true);
    try {
      let picked: PickedLocation | null = null;
      // Coordinates in the text / a full URL need no server round-trip
      // beyond reverse geocoding; short links (maps.app.goo.gl, apple.co)
      // are expanded server-side.
      const local = coords || parseMapsLinkCoordinates(text);
      if (local) {
        const geocoded = await getAddressFromCoordinates(local.latitude, local.longitude);
        picked = { ...local, address: geocoded.formatted, country: geocoded.country, city: geocoded.city };
      } else {
        const res = await apiService.resolveMapsLink((text.match(/https?:\/\/\S+/)?.[0] || text).trim());
        if (res.statusCode === 1000 && res.data) {
          picked = {
            latitude: res.data.latitude,
            longitude: res.data.longitude,
            address: res.data.address || `${res.data.latitude.toFixed(5)}, ${res.data.longitude.toFixed(5)}`,
            country: res.data.country,
            city: res.data.city,
          };
        }
      }
      if (seq !== linkSeqRef.current) return true; // superseded by newer input
      if (!picked) {
        setLinkError(t('mapPicker.linkFailed'));
        return true;
      }
      setSelectedLocation(picked);
      setSearchQuery(picked.address);
      centerMapOn(picked.latitude, picked.longitude);
    } catch {
      if (seq === linkSeqRef.current) setLinkError(t('mapPicker.linkFailed'));
    } finally {
      if (seq === linkSeqRef.current) setResolvingLink(false);
    }
    return true;
  };

  // Handle search input change with debouncing
  const handleSearchInputChange = (text: string) => {
    setSearchQuery(text);
    setLinkError(null);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // A pasted maps link / coordinates jumps straight there.
    if (looksLikeMapsLink(text) || parseCoordinateText(text)) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      searchTimeoutRef.current = setTimeout(() => { resolveLinkOrCoordinates(text); }, 400);
      return;
    }
    
    // Debounce the suggestion fetch
    searchTimeoutRef.current = setTimeout(() => {
      fetchSearchSuggestions(text);
    }, 300);
  };

  // Handle selecting a suggestion — builds the address straight from the
  // Place record's own fields rather than firing a fresh reverse-geocode
  // call, since a Place already carries locality/address_freeform/country.
  const handleSelectSuggestion = (suggestion: Place) => {
    const displayName = suggestion.name_primary || suggestion.name_en || suggestion.name_ar || '';
    setSearchQuery(displayName);
    setShowSuggestions(false);
    setSearchSuggestions([]);

    const { latitude, longitude } = suggestion;
    const addressParts = [displayName, suggestion.address_freeform, suggestion.locality].filter(Boolean);

    const locationData = {
      latitude,
      longitude,
      address: addressParts.join(', ') || 'Unknown Location',
      city: suggestion.locality || undefined,
    };

    setSelectedLocation(locationData);
    
    // Center map on selected location
    setTimeout(() => {
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(JSON.stringify({
            type: 'centerMap',
            latitude,
            longitude,
          }), '*');
        }
      } else if (webViewRef.current) {
        webViewRef.current.postMessage(JSON.stringify({
          type: 'centerMap',
          latitude,
          longitude,
        }));
      }
    }, 500);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    if (await resolveLinkOrCoordinates(searchQuery)) return;

    // Hide suggestions
    setShowSuggestions(false);
    setSearchSuggestions([]);

    try {
      setIsLoading(true);

      // Backed by the self-hosted Nominatim instance via our own backend
      // (same one fetchSearchSuggestions above uses), not the Overture
      // Places dataset — that endpoint is disabled in production (duckdb
      // native addon fails to compile against prod's GCC 13). Works
      // identically on web and native since it's just an authenticated
      // HTTP call, so there's no platform branch needed here the way the
      // old Places-vs-public-Nominatim-vs-device-geocoder split required.
      //
      // Previously this called apiService.searchPlaces (now-disabled route)
      // first — since that threw instead of returning no results, it was
      // caught by this function's own catch block below and surfaced a
      // "Search Error" alert, which meant the fallback logic beneath it
      // (public Nominatim on web, the device geocoder on native) never
      // actually ran. The Search button has been broken the same way the
      // suggestion dropdown was, just with a scarier-looking error.
      const suggestionResponse = await apiService.searchAddressSuggestions(searchQuery, 1);
      const topResult = suggestionResponse.data?.[0];

      if (topResult) {
        const { latitude, longitude } = topResult;
        const locationData = {
          latitude,
          longitude,
          address: topResult.address || 'Unknown Location',
          country: topResult.country,
          city: topResult.city,
        };
        setSelectedLocation(locationData);
        setTimeout(() => {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(JSON.stringify({
                type: 'centerMap',
                latitude,
                longitude,
              }), '*');
            }
          } else if (webViewRef.current) {
            webViewRef.current.postMessage(JSON.stringify({
              type: 'centerMap',
              latitude,
              longitude,
            }));
          }
        }, 500);
        setIsLoading(false);
        return;
      }

      // Nominatim found nothing — on native only, try the device's own
      // geocoder as a last resort (a genuinely independent data source, not
      // just a second call to the same backend). No equivalent fallback on
      // web; expo-location's geocodeAsync isn't reliably available there.
      let geocodeResult;

      if (Platform.OS !== 'web') {
        const geocode = await Location.geocodeAsync(searchQuery);
        if (geocode.length > 0) {
          geocodeResult = {
            latitude: geocode[0].latitude,
            longitude: geocode[0].longitude,
          };
        }
      }

      if (geocodeResult) {
        const { latitude, longitude } = geocodeResult;
        const geocoded = await getAddressFromCoordinates(latitude, longitude);

        const locationData = {
          latitude,
          longitude,
          address: geocoded.formatted,
          country: geocoded.country,
          city: geocoded.city,
        };

        setSelectedLocation(locationData);
        
        // Center map on search result
        setTimeout(() => {
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
            if (iframe && iframe.contentWindow) {
              iframe.contentWindow.postMessage(JSON.stringify({
                type: 'centerMap',
                latitude,
                longitude,
              }), '*');
            }
          } else if (webViewRef.current) {
            webViewRef.current.postMessage(JSON.stringify({
              type: 'centerMap',
              latitude,
              longitude,
            }));
          }
        }, 500);
      } else {
        Alert.alert(
          'Location Not Found',
          'Could not find the specified location. Please try a different search term.',
          [{ text: 'OK' }]
        );
      }
    } catch (error) {
      console.error('Search error:', error);
      Alert.alert(
        'Search Error',
        'Failed to search for the location. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Sends a message back down into the map iframe/WebView — mirrors the
  // dual-path pattern already used inline for 'centerMap' elsewhere in this
  // file, pulled out here since 'showPlaces' needs the exact same send from
  // an async fetch callback rather than a synchronous event handler.
  const postToPickerMap = (message: object) => {
    const payload = JSON.stringify(message);
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
      iframe?.contentWindow?.postMessage(payload, '*');
    } else {
      webViewRef.current?.postMessage(payload);
    }
  };

  const handleMapMessage = (event: any) => {
    try {
      let data;
      if (Platform.OS === 'web') {
        // Handle postMessage from iframe
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } else {
        // Handle WebView message
        data = JSON.parse(event.nativeEvent.data);
      }

      if (data.type === 'locationSelected') {
        const { latitude, longitude } = data;
        getAddressFromCoordinates(latitude, longitude).then(geocoded => {
          setSelectedLocation({
            latitude,
            longitude,
            address: geocoded.formatted,
            country: geocoded.country,
            city: geocoded.city,
          });
        });
      } else if (data.type === 'requestPlaces') {
        // Two independent pin layers, combined into one list for the map —
        // business contacts (this business's own saved data, via the
        // regular contacts list endpoint's nearLatitude/nearLongitude/
        // radiusKm params) and generic places (Overpass; see
        // overpass-places.service.ts for why Nominatim/Overture Places
        // aren't used here). Each pin carries `kind` so showPlaces can style
        // them differently and poiSelected can tell them apart — a
        // foundation for more layers later (vendors, orders, ...) rather
        // than a one-off.
        const { latitude, longitude } = data;
        const requestSeq = ++nearbyPinsRequestSeqRef.current;
        Promise.all([
          apiService.getParentContacts({
            nearLatitude: latitude,
            nearLongitude: longitude,
            radiusKm: 2,
            radiusFilterEnabled: true,
            includeUnlocated: false,
            limit: 40,
          }),
          apiService.getNearbyOverpassPlaces({ latitude, longitude, radiusMeters: 800, limit: 40 }),
        ])
          .then(([contactsResponse, placesResponse]) => {
            // A newer requestPlaces call (the user panned again while this
            // one was still in flight) has already superseded this result —
            // applying it now would only wipe out whatever the newer
            // request already drew, or hasn't drawn yet, with stale data.
            if (requestSeq !== nearbyPinsRequestSeqRef.current) return;
            const contactPins = (contactsResponse.data?.contacts || [])
              .filter((c: any) => c?.profile?.location?.coordinates)
              .map((c: any) => ({
                kind: 'contact',
                id: c._id,
                latitude: c.profile.location.coordinates.latitude,
                longitude: c.profile.location.coordinates.longitude,
                name: c.profile.name || 'Contact',
                category: '',
              }));
            const placePins = (placesResponse.data || []).map((p) => ({
              kind: 'place',
              id: p.id,
              latitude: p.latitude,
              longitude: p.longitude,
              name: p.name,
              category: p.category,
            }));
            postToPickerMap({ type: 'showPlaces', places: [...contactPins, ...placePins] });
          })
          .catch((error) => {
            console.warn('[MapLocationPicker] Nearby pins fetch failed:', error);
          });
      } else if (data.type === 'poiSelected') {
        const { latitude, longitude, name } = data;
        setSelectedLocation({ latitude, longitude, address: name || 'Selected place' });
      }
    } catch (error) {
      console.error('Error handling map message:', error);
    }
  };

  const handleConfirm = () => {
    if (selectedLocation) {
      onLocationSelect(selectedLocation);
      onClose();
    } else {
      Alert.alert(
        'No Location Selected',
        'Please select a location on the map before confirming.',
        [{ text: 'OK' }]
      );
    }
  };

  const handleUseCurrentLocation = () => {
    if (currentLocation) {
      setSelectedLocation(currentLocation);
      setTimeout(() => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage(JSON.stringify({
              type: 'centerMap',
              latitude: currentLocation.latitude,
              longitude: currentLocation.longitude,
            }), '*');
          }
        } else if (webViewRef.current) {
          webViewRef.current.postMessage(JSON.stringify({
            type: 'centerMap',
            latitude: currentLocation.latitude,
            longitude: currentLocation.longitude,
          }));
        }
      }, 500);
    } else {
      getCurrentLocation();
    }
  };

  // HTML for the map
  const mapHTML = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" />
        <style>
            body { margin: 0; padding: 0; }
            #map { height: 100vh; width: 100vw; }
            .location-marker {
                background: #007AFF;
                border: 3px solid white;
                border-radius: 50%;
                width: 20px;
                height: 20px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.3);
            }
            .poi-marker {
                background: #ff6b6b;
                border: 2px solid white;
                border-radius: 50%;
                width: 12px;
                height: 12px;
                box-shadow: 0 1px 5px rgba(0,0,0,0.2);
            }
            .leaflet-popup-content {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                font-size: 14px;
            }
        </style>
    </head>
    <body>
        <div id="map"></div>
        <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"></script>
        <script>
            let map;
            let marker;
            let isMapReady = false;

            function initMap() {
                // Start with a default location (San Francisco) at zoom level 10
                map = L.map('map').setView([37.7749, -122.4194], 10);
                
                // Use OpenStreetMap with better POI data
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '© OpenStreetMap contributors',
                    maxZoom: 19
                }).addTo(map);
                
                // Add additional layers for better POI visibility
                const cartoLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
                    attribution: '© OpenStreetMap contributors, © CARTO',
                    maxZoom: 19
                });
                
                // Add layer control for switching between map styles
                const baseMaps = {
                    "OpenStreetMap": L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                        attribution: '© OpenStreetMap contributors',
                        maxZoom: 19
                    }),
                    "CartoDB Voyager": cartoLayer
                };
                
                L.control.layers(baseMaps).addTo(map);

                // Add click handler
                map.on('click', function(e) {
                    const { lat, lng } = e.latlng;
                    
                    if (marker) {
                        map.removeLayer(marker);
                    }
                    
                    marker = L.marker([lat, lng], {
                        icon: L.divIcon({
                            className: 'location-marker',
                            html: '<div></div>',
                            iconSize: [20, 20]
                        })
                    }).addTo(map);
                    
                    // Send location to parent (React Native WebView or iframe parent)
                    const message = JSON.stringify({
                        type: 'locationSelected',
                        latitude: lat,
                        longitude: lng
                    });
                    
                    if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(message);
                    } else if (window.parent) {
                        window.parent.postMessage(message, '*');
                    }
                });
                
                // Nearby POIs come from the app's own /api/places/nearby
                // (Overture Places, EG/SA/PS) rather than a client-side
                // Overpass fetch — that endpoint needs an auth header the
                // WebView/iframe can't attach itself, so this only ever
                // POSTS a request up to React (see requestPlaces below);
                // React does the actual fetch and pushes results back via a
                // 'showPlaces' message (see handleParentMessage).
                function requestPlaces(lat, lng) {
                    const message = JSON.stringify({ type: 'requestPlaces', latitude: lat, longitude: lng });
                    if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(message);
                    } else if (window.parent) {
                        window.parent.postMessage(message, '*');
                    }
                }

                function showPlaces(places) {
                    if (window.poiMarkers) {
                        window.poiMarkers.forEach(marker => map.removeLayer(marker));
                    }
                    window.poiMarkers = [];
                    if (!Array.isArray(places)) return;
                    places.forEach(function (place) {
                        if (typeof place.latitude !== 'number' || typeof place.longitude !== 'number') return;
                        // Two pin layers, colored apart: business contacts (blue)
                        // vs. generic places (red) — see requestPlaces' handler on
                        // the React side for where 'kind' comes from.
                        const isContact = place.kind === 'contact';
                        const color = isContact ? '#4a90e2' : '#ff6b6b';
                        const poiMarker = L.marker([place.latitude, place.longitude], {
                            icon: L.divIcon({
                                className: isContact ? 'contact-marker' : 'poi-marker',
                                html: '<div style="background: ' + color + '; border: 2px solid white; border-radius: 50%; width: 12px; height: 12px;"></div>',
                                iconSize: [12, 12]
                            })
                        }).addTo(map);
                        window.poiMarkers.push(poiMarker);
                        const name = place.name || 'Place';
                        const category = place.category || '';
                        poiMarker.bindPopup('<b>' + name + '</b><br><small>' + category + '</small>');
                        poiMarker.on('click', function () {
                            const message = JSON.stringify({
                                type: 'poiSelected',
                                kind: place.kind,
                                id: place.id,
                                latitude: place.latitude,
                                longitude: place.longitude,
                                name: name,
                            });
                            if (window.ReactNativeWebView) {
                                window.ReactNativeWebView.postMessage(message);
                            } else if (window.parent) {
                                window.parent.postMessage(message, '*');
                            }
                        });
                    });
                }

                // Request nearby places when the map is moved (debounced)
                let poiSearchTimeout;
                map.on('moveend', function() {
                    clearTimeout(poiSearchTimeout);
                    poiSearchTimeout = setTimeout(() => {
                        const center = map.getCenter();
                        requestPlaces(center.lat, center.lng);
                    }, 1000); // 1 second debounce
                });

                isMapReady = true;

                // Inform parent that the map is ready
                const message = JSON.stringify({ type: 'mapReady' });
                if (window.ReactNativeWebView) {
                    window.ReactNativeWebView.postMessage(message);
                } else if (window.parent) {
                    window.parent.postMessage(message, '*');
                }

                // Request places at the initial location
                const initialCenter = map.getCenter();
                requestPlaces(initialCenter.lat, initialCenter.lng);
            }

            // Listen for messages from parent (React Native WebView or iframe parent)
            function handleParentMessage(event) {
                try {
                    const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
                    if (data.type === 'showPlaces') {
                        showPlaces(data.places);
                        return;
                    }
                    if (data.type === 'centerMap' && isMapReady) {
                        // Center map on the provided coordinates
                        map.setView([data.latitude, data.longitude], 15);
                        
                        // Remove existing marker
                        if (marker) {
                            map.removeLayer(marker);
                        }
                        
                        // Add new marker at the location
                        marker = L.marker([data.latitude, data.longitude], {
                            icon: L.divIcon({
                                className: 'location-marker',
                                html: '<div></div>',
                                iconSize: [20, 20]
                            })
                        }).addTo(map);
                        
                        // Send location selected event to parent
                        const message = JSON.stringify({
                            type: 'locationSelected',
                            latitude: data.latitude,
                            longitude: data.longitude
                        });
                        
                        // Try React Native WebView first, then fallback to window.postMessage for iframe
                        if (window.ReactNativeWebView) {
                            window.ReactNativeWebView.postMessage(message);
                        } else if (window.parent) {
                            window.parent.postMessage(message, '*');
                        }
                    }
                } catch (error) {
                    console.error('Error handling message:', error);
                }
            }
            
            // Listen for messages from React Native WebView
            if (window.ReactNativeWebView) {
                document.addEventListener('message', handleParentMessage);
            }
            
            // Listen for messages from iframe parent (web)
            window.addEventListener('message', handleParentMessage);

            // Initialize map when page loads
            document.addEventListener('DOMContentLoaded', initMap);
        </script>
    </body>
    </html>
  `;

  const pickerContent = (
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={onClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color={colors.text.primary} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Select Location</Text>
          <TouchableOpacity 
            onPress={handleConfirm} 
            style={[
              styles.confirmButton,
              !selectedLocation && styles.confirmButtonDisabled
            ]}
            disabled={!selectedLocation}
          >
            <Text style={[
              styles.confirmButtonText,
              !selectedLocation && styles.confirmButtonTextDisabled
            ]}>
              Confirm
            </Text>
          </TouchableOpacity>
        </View>

        {/* Search Bar */}
        <View style={styles.searchContainer}>
          <View style={styles.searchInputWrapper}>
            <View style={styles.searchInputContainer}>
              <Ionicons name="search" size={20} color={colors.text.secondary} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder={t('mapPicker.searchPlaceholder')}
                placeholderTextColor={colors.text.secondary}
                value={searchQuery}
                onChangeText={handleSearchInputChange}
                onSubmitEditing={handleSearch}
                returnKeyType="search"
                onFocus={() => {
                  if (searchSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  // Delay hiding suggestions to allow for suggestion selection
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity 
                  onPress={() => {
                    setSearchQuery('');
                    setSearchSuggestions([]);
                    setShowSuggestions(false);
                  }} 
                  style={styles.clearButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.text.secondary} />
                </TouchableOpacity>
              )}
            </View>
            {(resolvingLink || linkError) && (
              <View style={styles.linkStatus}>
                {resolvingLink ? <ActivityIndicator size="small" color={colors.primary} /> : <Ionicons name="alert-circle" size={16} color={colors.error} />}
                <Text style={[styles.linkStatusText, !!linkError && !resolvingLink && { color: colors.error }]} numberOfLines={2}>
                  {resolvingLink ? t('mapPicker.resolvingLink') : linkError}
                </Text>
              </View>
            )}
            
            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <View style={styles.suggestionsContainer}>
                {searchSuggestions.map((suggestion) => (
                  <TouchableOpacity
                    key={suggestion.overture_id}
                    style={styles.suggestionItem}
                    onPress={() => handleSelectSuggestion(suggestion)}
                  >
                    <Ionicons name="location" size={16} color={colors.text.secondary} style={styles.suggestionIcon} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.suggestionText} numberOfLines={1}>
                        {suggestion.name_primary || suggestion.name_en || suggestion.name_ar}
                      </Text>
                      {!!(suggestion.locality || suggestion.address_freeform) && (
                        <Text style={styles.suggestionSubtext} numberOfLines={1}>
                          {[suggestion.address_freeform, suggestion.locality].filter(Boolean).join(', ')}
                        </Text>
                      )}
                    </View>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
          <TouchableOpacity onPress={handleSearch} style={styles.searchButton}>
            <Ionicons name="search" size={20} color={colors.white} />
          </TouchableOpacity>
        </View>

        {/* Map */}
        <View style={styles.mapContainer}>
          {Platform.OS === 'web' ? (
            <iframe
              id="map-iframe"
              srcDoc={mapHTML}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                position: 'relative',
                zIndex: 1,
                pointerEvents: showSuggestions ? ('none' as any) : ('auto' as any),
              } as any}
              onLoad={() => {
                // Map is loaded, now get current location if we have it
                if (currentLocation) {
                  setTimeout(() => {
                    if (typeof window !== 'undefined') {
                      const iframe = document.getElementById('map-iframe') as HTMLIFrameElement;
                      if (iframe && iframe.contentWindow) {
                        iframe.contentWindow.postMessage(JSON.stringify({
                          type: 'centerMap',
                          latitude: currentLocation.latitude,
                          longitude: currentLocation.longitude,
                        }), '*');
                      }
                    }
                  }, 500);
                }
              }}
            />
          ) : (
            <WebView
              ref={webViewRef}
              source={{ html: mapHTML }}
              style={styles.map}
              onMessage={handleMapMessage}
              onLoadEnd={() => {
                // Map is loaded, now get current location if we have it
                if (currentLocation) {
                  setTimeout(() => {
                    if (webViewRef.current) {
                      webViewRef.current.postMessage(JSON.stringify({
                        type: 'centerMap',
                        latitude: currentLocation.latitude,
                        longitude: currentLocation.longitude,
                      }));
                    }
                  }, 500);
                }
              }}
              javaScriptEnabled={true}
              domStorageEnabled={true}
              startInLoadingState={true}
              renderLoading={() => (
                <View style={styles.mapLoadingOverlay}>
                  <ActivityIndicator size="large" color={colors.primary} />
                  <Text style={styles.mapLoadingText}>Loading Map...</Text>
                </View>
              )}
            />
          )}
          
          {/* Loading Overlay */}
          {isLoading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color={colors.primary} />
              <Text style={styles.loadingText}>Loading...</Text>
            </View>
          )}
        </View>

        {/* Location Info */}
        <View style={styles.locationInfo}>
          <View style={styles.locationIconContainer}>
            <Ionicons name="location" size={20} color={colors.primary} />
          </View>
          <View style={styles.locationDetails}>
            <Text style={styles.locationAddress} numberOfLines={2}>
              {selectedLocation?.address || 'Tap on the map to select a location'}
            </Text>
            {selectedLocation && (
              <Text style={styles.locationCoordinates}>
                {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
              </Text>
            )}
          </View>
          <TouchableOpacity onPress={handleUseCurrentLocation} style={styles.currentLocationButton}>
            <Ionicons name="locate" size={20} color={colors.primary} />
          </TouchableOpacity>
        </View>
      </View>
  );

  if (Platform.OS === 'web') {
    // On web: use createPortal to inject into document.body so position:fixed
    // escapes any parent overflow:hidden / transform clipping (mobile browsers)
    if (typeof document === 'undefined') return null;
    const ReactDOM = require('react-dom');
    if (!visible) return null;
    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          backgroundColor: 'rgba(0,0,0,0.6)',
        }}
      >
        {/* Backdrop */}
        <div
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={onClose}
        />
        {/* Right pane — full viewport height, docked to the right edge,
            replacing the old bottom-sheet so the map gets real vertical
            room to work with instead of being squeezed under a header +
            search bar + location-info footer in a ~92dvh sheet. */}
        <div
          style={{
            position: 'relative',
            height: '100%',
            width: 420,
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: colors.background.surface as string,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.25)',
            overflow: 'hidden',
          }}
        >
          {pickerContent}
        </div>
      </div>,
      document.body,
    );
  }

  // Native: use Modal with slide animation
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.nativeFill}>
        {pickerContent}
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  linkStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  linkStatusText: {
    flex: 1,
    fontSize: 12,
    color: colors.text.secondary,
  },
  nativeFill: {
    flex: 1,
  },
  webOverlay: {
    ...Platform.select({
      web: {
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9998,
        display: 'flex' as any,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
      },
      default: { flex: 1 },
    }),
  } as any,
  webBackdrop: {
    ...Platform.select({
      web: {
        position: 'absolute' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
      },
      default: {},
    }),
  } as any,
  webContainer: {
    ...Platform.select({
      web: {
        position: 'relative' as any,
        width: '100%' as any,
        maxWidth: 600,
        height: '90%' as any,
        maxHeight: 700,
        borderRadius: designTokens.borderRadius.lg,
        overflow: 'hidden',
        zIndex: 2,
        ...designTokens.shadows.lg,
      },
      default: { flex: 1 },
    }),
  } as any,
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  closeButton: {
    padding: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600' as any,
    color: colors.text.primary,
  },
  confirmButton: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.primary,
  },
  confirmButtonDisabled: {
    backgroundColor: colors.lightGray,
  },
  confirmButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: '500' as any,
  },
  confirmButtonTextDisabled: {
    color: colors.text.secondary,
  },
  searchContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.background.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...Platform.select({
      web: {
        position: 'relative' as any,
        zIndex: 1001,
      },
      default: {
        zIndex: 1001,
      },
    }),
  },
  searchInputWrapper: {
    flex: 1,
    marginRight: spacing.sm,
    position: 'relative',
    ...Platform.select({
      web: {
        zIndex: 1002,
      },
      default: {
        zIndex: 1002,
      },
    }),
  },
  searchInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.lightGray,
    borderRadius: 25,
    paddingHorizontal: spacing.md,
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    ...designTokens.shadows.md,
    maxHeight: 200,
    overflow: 'hidden',
    ...Platform.select({
      web: {
        zIndex: 1003,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        elevation: 5,
        pointerEvents: 'auto',
        position: 'absolute' as any,
      },
      default: {
        zIndex: 1003,
        elevation: 5,
      },
    }),
  },
  suggestionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  suggestionIcon: {
    marginRight: spacing.sm,
  },
  suggestionText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  suggestionSubtext: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    marginTop: 2,
  },
  searchIcon: {
    marginRight: spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    paddingVertical: spacing.sm,
  },
  clearButton: {
    padding: spacing.xs,
  },
  searchButton: {
    padding: spacing.md,
    borderRadius: 25,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
    ...Platform.select({
      web: {
        zIndex: 1,
      },
      default: {
        zIndex: 1,
      },
    }),
  },
  map: {
    flex: 1,
    ...Platform.select({
      web: {
        zIndex: 1,
        position: 'relative' as any,
      },
      default: {
        zIndex: 1,
      },
    }),
  },
  mapLoadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  mapLoadingText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  loadingText: {
    marginTop: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
  },
  locationInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.background.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  locationIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  locationDetails: {
    flex: 1,
  },
  locationAddress: {
    fontSize: typography.fontSize.base,
    fontWeight: '500' as any,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  locationCoordinates: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
  },
  currentLocationButton: {
    padding: spacing.sm,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
  },
});
